import { formatDateTime, formatDateTimeFull } from "@/lib/format";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { glopicApi } from "@/lib/glopic-api";

interface GenDetail {
  gen_id: string;
  user_id: string;
  user_type: "consumer" | "merchant" | null;
  nickname: string | null;
  template_id: string | null;
  style_id: string | null;
  ratio_id: string | null;
  prompt: string | null;
  task_status: string;
  image_url: string | null;
  display_image_url: string | null;
  original_image_url: string | null;
  image_url_type: string | null;
  hd_image_url: string | null;
  model_id: string | null;
  tool_id: string | null;
  mode: string | null;
  entry_id: string | null;
  source_entry: string | null;
  generation_operation: string | null;
  quality: string | null;
  cost_points: number;
  cached: boolean;
  failure_message: string | null;
  created_at: string;
  updated_at: string;
  tasks?: TaskDetail[];
}

interface TaskDetail {
  id: number;
  status: string;
  retry_count: number;
  max_retry: number;
  worker_id: string | null;
  celery_task_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

const statusLabels: Record<string, { cls: string; label: string; icon: string }> = {
  created: { cls: "bg-slate-500/10 text-slate-600", label: "已创建", icon: "ri-add-circle-line" },
  queued: { cls: "bg-amber-500/10 text-amber-600", label: "排队中", icon: "ri-hourglass-line" },
  generating: { cls: "bg-brand/10 text-brand", label: "生成中", icon: "ri-loader-2-line" },
  auditing: { cls: "bg-cyan-500/10 text-cyan-600", label: "审核中", icon: "ri-shield-check-line" },
  success: { cls: "bg-emerald-500/10 text-emerald-500", label: "成功", icon: "ri-checkbox-circle-line" },
  failed: { cls: "bg-red-500/10 text-red-500", label: "失败", icon: "ri-close-circle-line" },
  blocked: { cls: "bg-orange-500/10 text-orange-600", label: "已拦截", icon: "ri-forbid-line" },
  timeout: { cls: "bg-red-500/10 text-red-600", label: "已超时", icon: "ri-timer-line" },
  cancelled: { cls: "bg-slate-500/10 text-slate-500", label: "已取消", icon: "ri-close-circle-line" },
  refunded: { cls: "bg-violet-500/10 text-violet-600", label: "已退款", icon: "ri-refund-2-line" },
  pending: { cls: "bg-amber-500/10 text-amber-500", label: "等待中", icon: "ri-time-line" },
  processing: { cls: "bg-brand/10 text-brand", label: "处理中", icon: "ri-loader-2-line" },
};

const operationLabel = (operation: string | null | undefined) => {
  if (operation === "image_generate") return "生图(image_generate)";
  if (operation === "image_edit") return "图片编辑(image_edit)";
  return "--";
};

export default function AiGenerationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [gen, setGen] = useState<GenDetail | null>(null);
  const [tasks, setTasks] = useState<TaskDetail[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await glopicApi.get(`/generations/${id}`);
      const data = response.data.data;
      setGen(data);
      setTasks(data.tasks ?? []);
    } catch (err) {
      console.error("Generation detail error:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <i className="ri-loader-4-line animate-spin text-3xl text-brand" />
      </div>
    );
  }

  if (!gen) {
    return (
      <div className="text-center py-20">
        <i className="ri-image-ai-line text-4xl text-text-muted mb-3" />
        <p className="text-text-secondary">生成记录不存在</p>
        <button onClick={() => navigate("/ai/generations")} className="mt-4 text-brand text-sm hover:underline cursor-pointer">
          返回生成记录
        </button>
      </div>
    );
  }

  const statusCfg = statusLabels[gen.task_status] || {
    cls: "bg-text-muted/10 text-text-muted",
    label: gen.task_status || "未知状态",
    icon: "ri-question-line",
  };
  const displayImageUrl = gen.display_image_url ?? gen.image_url;
  const originalImageUrl = gen.original_image_url ?? gen.image_url;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/ai/generations")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-surface text-text-secondary transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">生成详情</h2>
            <p className="mt-1 text-xs font-mono text-text-muted" data-testid="generation-id">
              {gen.gen_id}
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${statusCfg.cls}`}>
          <i className={statusCfg.icon} />
          {statusCfg.label}
        </span>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Image + basic info */}
        <div className="lg:col-span-2 space-y-5">
          {/* Image preview */}
          <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
            <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
              <h3 className="text-sm font-medium text-text-primary">生成结果</h3>
              {originalImageUrl && (
                <a href={originalImageUrl} target="_blank" rel="noopener noreferrer" className="text-brand text-xs hover:underline flex items-center gap-1">
                  <i className="ri-external-link-line" />
                  查看原图
                </a>
              )}
            </div>
            <div className="p-5 flex items-center justify-center bg-surface/50">
              {displayImageUrl ? (
                <div className="relative">
                  <SignedImage src={displayImageUrl} alt="生成结果" className="max-w-full max-h-[500px] rounded-lg object-contain" />
                  {gen.cached && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-brand/10 text-brand">
                      <i className="ri-refresh-line" /> 缓存
                    </span>
                  )}
                </div>
              ) : (
                <div className="py-16 text-center">
                  <i className="ri-image-line text-4xl text-text-muted mb-3" />
                  <p className="text-text-muted text-sm">生成失败，无输出图片</p>
                  {gen.failure_message && (
                    <p className="text-red-500 text-xs mt-2">{gen.failure_message}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Parameters */}
          <div className="bg-white rounded-xl border border-surface-border p-5">
            <h3 className="text-sm font-medium text-text-primary mb-4">生成参数</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">模板</span>
                <span className="text-text-primary">{gen.template_id || "--"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">风格</span>
                <span className="text-text-primary">{gen.style_id || "--"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">比例</span>
                <span className="text-text-primary">{gen.ratio_id || "--"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">模型</span>
                <span className="text-text-primary">{gen.model_id || "--"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">来源</span>
                <span className="text-text-primary">{gen.source_entry || "--"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">入口</span>
                <span className="text-text-primary">{gen.entry_id || "--"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">工具</span>
                <span className="text-text-primary">{gen.tool_id || "--"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">执行类型</span>
                <span className="text-text-primary">{operationLabel(gen.generation_operation)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">画质</span>
                <span className="text-text-primary">{gen.quality || "--"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">消耗积分</span>
                <span className="text-text-primary font-mono">{gen.cost_points} pts</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">缓存</span>
                <span className="text-text-primary">{gen.cached ? "是" : "否"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-surface-border">
                <span className="text-text-muted">生成时间</span>
                <span className="text-text-muted text-xs">{formatDateTimeFull(gen.created_at)}</span>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-text-muted text-xs mb-1.5">提示词</p>
              <div className="bg-surface rounded-lg p-3 text-sm text-text-primary leading-relaxed">
                {gen.prompt || "--"}
              </div>
            </div>

            {gen.failure_message && (
              <div className="mt-4">
                <p className="text-text-muted text-xs mb-1.5">失败原因</p>
                <div className="bg-red-50 rounded-lg p-3 text-sm text-red-500 leading-relaxed">
                  {gen.failure_message}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: User + Tasks */}
        <div className="space-y-5">
          {/* User info */}
          <div className="bg-white rounded-xl border border-surface-border p-5">
            <h3 className="text-sm font-medium text-text-primary mb-3">用户信息</h3>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center text-brand font-medium text-sm">
                {(gen.nickname || "?")[0]}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-text-primary text-sm font-medium">{gen.nickname || gen.user_id}</p>
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] ${gen.user_type === "merchant" ? "bg-violet-500/10 text-violet-600" : "bg-blue-500/10 text-blue-600"}`}>
                    {gen.user_type === "merchant" ? "商家用户" : gen.user_type === "consumer" ? "C端用户" : "未知类型"}
                  </span>
                </div>
                <p className="text-text-muted text-xs font-mono">{gen.user_id}</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/ai/users/${gen.user_id}`)}
              className="w-full py-2 rounded-lg text-sm text-brand border border-brand/20 hover:bg-brand/5 transition-colors cursor-pointer"
            >
              查看用户详情
            </button>
          </div>

          {/* Task timeline */}
          <div className="bg-white rounded-xl border border-surface-border p-5">
            <h3 className="text-sm font-medium text-text-primary mb-4">执行链路</h3>
            {tasks.length === 0 ? (
              <p className="text-text-muted text-sm text-center py-4">暂无子任务记录</p>
            ) : (
              <div className="space-y-4">
                {tasks.map((t, i) => (
                  <div key={t.id} className="relative pl-6">
                    {i < tasks.length - 1 && (
                      <div className="absolute left-[9px] top-6 w-[2px] h-[calc(100%+8px)] bg-surface-border" />
                    )}
                    <div className={`absolute left-0 top-1 w-5 h-5 rounded-full flex items-center justify-center ${
                      t.status === "success" ? "bg-emerald-500/10" : t.status === "failed" ? "bg-red-500/10" : "bg-amber-500/10"
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        t.status === "success" ? "bg-emerald-500" : t.status === "failed" ? "bg-red-500" : "bg-amber-500"
                      }`} />
                    </div>
                    <div className="pb-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-text-primary">
                          执行 #{t.retry_count > 0 ? `${t.retry_count + 1} (重试)` : "1"}
                        </span>
                        <TaskStatusBadge status={t.status} />
                      </div>
                      <div className="space-y-1 text-xs text-text-muted">
                        {t.worker_id && <p>Worker: {t.worker_id}</p>}
                        {t.celery_task_id && <p className="font-mono truncate">Celery: {t.celery_task_id.slice(0, 16)}...</p>}
                        {t.started_at && <p>开始: {formatDateTime(t.started_at)}</p>}
                        {t.completed_at && <p>完成: {formatDateTime(t.completed_at)}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskStatusBadge({ status }: { status: string }) {
  const config: Record<string, { cls: string; label: string }> = {
    success: { cls: "bg-emerald-500/10 text-emerald-500", label: "成功" },
    failed: { cls: "bg-red-500/10 text-red-500", label: "失败" },
    blocked: { cls: "bg-orange-500/10 text-orange-600", label: "已拦截" },
    timeout: { cls: "bg-red-500/10 text-red-600", label: "已超时" },
    cancelled: { cls: "bg-slate-500/10 text-slate-500", label: "已取消" },
    refunded: { cls: "bg-violet-500/10 text-violet-600", label: "已退款" },
    pending: { cls: "bg-amber-500/10 text-amber-500", label: "等待中" },
  };
  const c = config[status] || { cls: "bg-text-muted/10 text-text-muted", label: status };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${c.cls}`}>
      {c.label}
    </span>
  );
}

function SignedImage({ src, alt, className }: { src: string; alt: string; className: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="w-64 max-w-full rounded-lg border border-dashed border-surface-border bg-white px-5 py-10 text-center text-text-muted">
        <i className="ri-image-close-line mb-2 block text-3xl" />
        <p className="text-sm">图片加载失败</p>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />;
}
